import {
  Checkbox,
  FormControlLabel,
  Grid,
  InputAdornment,
  TextField
} from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import { Money } from "@material-ui/icons";
import { Alert } from "@material-ui/lab";
import { useContext } from "react";
import { UserContext } from "../../../../UserProvider";
import { DepositFeeEditor } from "../../../../components/inputs/DepositFeeEditor";
import { Currency } from "../../../../components/misc/Currency";
import { PriceUnitSelect } from "../../../../components/selects/PriceUnitSelect";
import { VatSelect } from "../../../../components/selects/VatSelect";
import { useI18n } from "../../../../hooks/useI18n";
import {
  isWeightProduct,
  Product,
  ProductDepositFee
} from "../../../../models/Product";
import { Authenticated } from "../../../../models/User";
import { parseNumberStrict } from "../../../../utils";
import { SectionCard } from "../SectionCard";
import useStyles from "./styles";

interface Props {
  product: Product;
  onPriceUnitChange: (val: any) => void;
  onPriceChange: (val: any) => void;
  onMinPriceChange: (val: any) => void;
  onVatChange: (percentage: any, amount: any) => void;
  onAcquisitionPriceChange: (val: any) => void;
  onDepositFeeChange: (val: any) => void;
  onOpenPricingChange: (val: any) => void;
}

export const PricingSection = ({
  product,
  onPriceUnitChange,
  onPriceChange,
  onMinPriceChange,
  onVatChange,
  onAcquisitionPriceChange,
  onDepositFeeChange,
  onOpenPricingChange
}: Props) => {
  const classes = useStyles();
  const theme = useTheme();
  const currentUser = useContext(UserContext) as Authenticated;
  const { t } = useI18n();

  return (
    <SectionCard
      icon={<Money style={{ color: theme.palette.success.dark }} />}
      title={t("common.productDetails.pricing")}
    >
      <Grid container spacing={4}>
        {currentUser.tenant.configuration?.features?.usesOpenPricing && (
          <Grid item xs={12} style={{ paddingTop: 20, paddingBottom: 0 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={product.pricing.isOpenPricing}
                  onChange={e => onOpenPricingChange(e.target.checked)}
                />
              }
              label={t("common.formFields.openPricing")}
            />
          </Grid>
        )}
        <Grid item xs={12} md={4}>
          {product.pricing.isOpenPricing ? (
            <TextField
              className={classes.field}
              label={t("common.formFields.minimumPrice")}
              error={product.pricing.minimumPrice <= 0}
              type="number"
              value={product?.pricing.minimumPrice}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <PriceUnitSelect
                      disabled={isWeightProduct(product?.gtin)}
                      label={""}
                      className={classes.field}
                      value={product?.pricing?.priceUnit}
                      onSelected={priceUnit =>
                        onPriceUnitChange(priceUnit.unit as string)
                      }
                    />
                  </InputAdornment>
                )
              }}
              onChange={(e: any) =>
                onMinPriceChange(parseNumberStrict(e.target.value))
              }
            />
          ) : (
            <TextField
              className={classes.field}
              label={t("common.formFields.price")}
              error={product.pricing.price <= 0}
              type="number"
              value={
                product.id === "00000000-0000-0000-0000-000000000000"
                  ? undefined
                  : product?.pricing.price
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <PriceUnitSelect
                      disabled={isWeightProduct(product?.gtin)}
                      label={""}
                      className={classes.field}
                      value={product?.pricing?.priceUnit}
                      onSelected={priceUnit => onPriceUnitChange(priceUnit)}
                    />
                  </InputAdornment>
                )
              }}
              onChange={(e: any) => onPriceChange(e.target.value)}
            />
          )}
        </Grid>
        <Grid item xs={12} md={2}>
          <VatSelect
            label={t("common.formFields.vat")}
            className={classes.field}
            error={
              product.pricing.vatPercentage == undefined ||
              isNaN(product.pricing.vatPercentage)
            }
            value={product?.pricing?.vatPercentage}
            onSelected={vat =>
              onVatChange(
                vat.decimalForm,
                product.pricing.price -
                  product.pricing.price / (1 + vat.decimalForm)
              )
            }
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            className={classes.field}
            label={t("common.formFields.acquisitionPrice")}
            type="number"
            value={product?.pricing.acquisitionPrice}
            onChange={(e: any) => onAcquisitionPriceChange(e.target.value)}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <DepositFeeEditor
            onChange={depositFee =>
              onDepositFeeChange({
                depositAmount: depositFee
              } as ProductDepositFee)
            }
            value={
              product.pricing.depositFee != null &&
              product.pricing.depositFee.depositAmount > 0
                ? product.pricing.depositFee.depositAmount
                : undefined
            }
          />
        </Grid>
        <Grid item xs={12} md={8}>
          {product?.pricing?.depositFee?.depositAmount &&
          product?.pricing?.depositFee?.depositAmount > 0 ? (
            <Alert severity={"info"}>
              {t("common.pricingSection.customerPays")}{" "}
              <Currency value={product?.pricing?.price} />+{" "}
              <Currency value={product?.pricing?.depositFee?.depositAmount} />{" "}
              {t("common.pricingSection.inDepositFee")}
            </Alert>
          ) : (
            <></>
          )}
        </Grid>
      </Grid>
    </SectionCard>
  );
};
